import { z } from 'zod';
import { ControllerRenderProps } from 'react-hook-form';
import { parseISO } from 'date-fns';

import { getDateNeededByValidationError } from './dateNeededBy';

export const submitFormSchema = z
  .object({
    notificationEmails: z
      .array(z.string().email('Please enter a valid email address'))
      .min(1, 'At least one email address is required'),
    referenceId: z
      .string()
      .max(512, 'Request Reference ID must be 512 characters or less')
      .optional(),
    requestReason: z.enum(['upcoming-appointment', 'information-gathering'], {
      message: 'Request reason is required',
    }),
    dateNeededBy: z.string().optional(),
  })
  .refine(
    (data) => {
      // Date is required only for upcoming appointment
      return (
        data.requestReason !== 'upcoming-appointment' || !!data.dateNeededBy
      );
    },
    {
      message: 'Date needed by is required for upcoming appointments',
      path: ['dateNeededBy'],
    },
  )
  .superRefine((data, context) => {
    if (data.requestReason !== 'upcoming-appointment' || !data.dateNeededBy) {
      return;
    }

    const validationError = getDateNeededByValidationError(
      parseISO(data.dateNeededBy),
    );

    if (validationError === 'not-after-today') {
      context.addIssue({
        code: 'custom',
        message: 'Date must be after today',
        path: ['dateNeededBy'],
      });
    }

    if (validationError === 'after-maximum') {
      context.addIssue({
        code: 'custom',
        message: 'Date must be within 60 days from today',
        path: ['dateNeededBy'],
      });
    }
  });

export type SubmitFormType = z.infer<typeof submitFormSchema>;

export type SubmitFormFieldProps<T extends keyof SubmitFormType> = {
  field: ControllerRenderProps<SubmitFormType, T>;
};
